import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import { User } from '../model/user';
import { Router } from '@angular/router';
import { Role } from '../enums/role';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUser = signal<User | null>(null);
  public currentUser$ = this.currentUser.asReadonly();
  private http = inject(HttpClient);
  private router = inject(Router);
  endpoint = `${environment.api}/auth`;

  saveToken(token: string) {
    localStorage.setItem('token', token);
  }
  saveRefreshToken(token: string) {
    if (token) {
      localStorage.setItem('refreshToken', token);
    }
  }
  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }
  getToken() {
    return localStorage.getItem('token');
  }
  logout() {
    // Revoque cote API la session longue (jeton de rafraichissement) avant de
    // nettoyer le poste. Envoi sans attendre : la deconnexion locale ne
    // depend pas de la reponse.
    const token = this.getToken();
    if (token && this.getRefreshToken()) {
      this.http
        .post(`${this.endpoint}/logout`, {}, { headers: { Authorization: `Bearer ${token}` } })
        .subscribe({ error: () => undefined });
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    this.currentUser.set(null);
    this.router.navigate(['/login']);
  }

  // ****************** Create new Diagnostic and user ******************************* //
  login(user: User): Observable<{ token: string; refreshToken: string; user: User }> {
    return this.http
      .post<{ token: string; refreshToken: string; user: User }>(`${this.endpoint}/login`, user)
      .pipe(
        tap((response) => {
          this.saveToken(response.token);
          this.saveRefreshToken(response.refreshToken);
          this.currentUser.set(response.user); // Stocke direct l'user reçu
          // Redirection basée sur le rôle
          const redirectPath = response.user.role === Role.admin ? '/admin' : '/apps/dashboard';
          this.router.navigate([redirectPath]);
        }),
      );
  }
  // ****************** GET USER CONECTED ******************************* //
  loadUserProfile(): Observable<User> {
    return this.http.get<User>(`${this.endpoint}/me`).pipe(
      tap((user) => this.currentUser.set(user)),
      catchError(() => {
        this.logout();
        return throwError(() => new Error('Failed to load profile'));
      }),
    );
  }
  // ****************** Refresh Token ******************************* //
  // L'API fait tourner le jeton de rafraichissement a chaque appel : le
  // nouveau remplace l'ancien, qui ne sert plus. refreshToken vaut null quand
  // un appel simultane a deja fait la rotation (on garde alors le stocke).
  refreshToken(): Observable<{ token: string; refreshToken?: string | null }> {
    const refreshToken = this.getRefreshToken();

    return this.http
      .post<{ token: string; refreshToken?: string | null }>(
        `${this.endpoint}/refresh-token`,
        {}, // Corps vide
        {
          headers: {
            'X-Refresh-Token': `Bearer ${refreshToken}`,
          },
        },
      )
      .pipe(tap((response) => this.saveRefreshToken(response.refreshToken ?? '')));
  }
  // ****************** Create new Diagnostic and user ******************************* //
  signUp(user: User): Observable<{ token: string; refreshToken: string; user: User }> {
    return this.http
      .post<{ token: string; refreshToken: string; user: User }>(`${this.endpoint}/signUp`, user)
      .pipe(
        tap((response) => {
          this.saveToken(response.token);
          this.saveRefreshToken(response.refreshToken);
          this.currentUser.set(response.user); // Stocke direct l'user reçu
          // Redirection basée sur le rôle
          const redirectPath = response.user.role === Role.admin ? '/admin' : '/apps/dashboard';
          this.router.navigate([redirectPath]);
        }),
      );
  }
  // ****************** Upadate password ******************************* //
  updatePassword(email, password, otp): Observable<{ token: string }> {
    return this.http.post<{ token: string }>(`${this.endpoint}/update-password`, {
      email,
      password,
      otp,
    });
  }
  // ****************** Send code comfirmation reset password ****************** //
  sendCode(email): Observable<null> {
    return this.http.post<null>(`${this.endpoint}/send-code`, { email });
  }

  // ****************** Invite User Espace client ******************************* //
  invite(user: User): Observable<null> {
    return this.http.post<null>(`${this.endpoint}/invite`, user);
  }
  isAuthenticated(): Observable<boolean> {
    return this.http.get<boolean>(`${this.endpoint}/is-authenticated`);
  }
  // ***************** Active Compte **************************** //
  activateUser(
    user: User,
    token: string,
  ): Observable<{ token: string; refreshToken: string; user: User }> {
    return this.http
      .patch<{
        token: string;
        refreshToken: string;
        user: User;
      }>(`${this.endpoint}/activate-user`, { user, token })
      .pipe(
        tap((response) => {
          this.saveToken(response.token);
          this.saveRefreshToken(response.refreshToken);
          this.currentUser.set(response.user); // Stocke direct l'user reçu
          // Redirection basée sur le rôle
          const redirectPath = response.user.role === Role.admin ? '/admin' : '/apps/dashboard';
          this.router.navigate([redirectPath]);
        }),
      );
  }
  // ***************** Vérify token activation ****************** //
  verifyToken(token: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.endpoint}/verify-token`, { token });
  }
}
